import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';
import { MaximizeIcon, MinimizeIcon, RefreshCwIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { SearchInput } from '@modules/_shared/components/search-input/SearchInput';
import { READ_PORTAL_IDX } from '@modules/portals/portalsQueries';
import { useLocale } from '@shared/hooks/locale';

const EXCLUDED_COLUMNS = new Set([
  'VERSION',
  'sender_id',
  'eol',
  // picture titles/descriptions/urls (1-13)
  ...Array.from({ length: 13 }, (_, i) => `picture_${i + 1}_title`),
  ...Array.from({ length: 13 }, (_, i) => `picture_${i + 1}_description`),
  ...Array.from({ length: 13 }, (_, i) => `picture_${i + 1}_url`),
  // billing fields
  'billing_anrede',
  'billing_first_name',
  'billing_name',
  'billing_company',
  'billing_street',
  'billing_post_box',
  'billing_zip',
  'billing_place_name',
  'billing_land',
  'billing_phone_1',
  'billing_phone_2',
  'billing_mobile',
  'billing_language',
  // misc empty fields
  'publishing_id',
  'delivery_id',
  'commission_sharing',
  'commission_own',
  'commission_partner',
  'agency_logo',
  'agency_logo_2',
  'advertisement_id',
  'sparefield_1',
  'sparefield_3',
  'sparefield_4',
]);

const WIDE_COLUMNS = new Set([
  'object_title',
  'object_description',
  'object_street',
  'url',
  'own_object_url',
  '360_url',
]);

type PortalIdxContentDialogProps = {
  portalId: string;
  fullScreen?: boolean;
  onToggleFullScreen?: () => void;
};

export const PortalIdxContentDialog: React.FC<PortalIdxContentDialogProps> = ({
  portalId,
  fullScreen = false,
  onToggleFullScreen,
}) => {
  const { t } = useLocale();
  const theme = useTheme();
  const [search, setSearch] = useState('');

  const { data, loading, error, refetch } = useQuery(READ_PORTAL_IDX, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: { id: portalId },
  });

  const handleFetch = () => {
    refetch({ id: portalId });
  };

  const rows = useMemo(() => {
    const rawRows = (data?.read_portal_idx?.rows ?? []) as Array<
      Record<string, unknown> & { lot_id: string | null; ref_object: string }
    >;
    const needle = search.toLowerCase();
    return rawRows
      .filter(
        row =>
          needle === '' ||
          (row.ref_object ?? '').toLowerCase().includes(needle),
      )
      .map((row, index) => ({
        ...row,
        _id: index,
      }));
  }, [data, search]);

  const columns: GridColDef[] = useMemo(() => {
    const serverColumns = (data?.read_portal_idx?.columns ?? []) as string[];

    const lotCol: GridColDef = {
      field: 'lot_id',
      headerName: 'Lot',
      width: 250,
      renderCell: params => {
        const lotId = params.value as string | null;
        if (lotId == null) {
          return (
            <Typography
              variant="body2"
              sx={{
                color: 'text.disabled',
              }}
            >
              —
            </Typography>
          );
        }
        return (
          <Link
            to={`/listings/${lotId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: (theme.vars ?? theme).palette.primary.main }}
          >
            {lotId}
          </Link>
        );
      },
    };

    const idxCols: GridColDef[] = serverColumns
      .filter(col => !EXCLUDED_COLUMNS.has(col))
      .map(col => ({
        field: col,
        headerName: col,
        width: WIDE_COLUMNS.has(col) ? 250 : 130,
      }));

    return [lotCol, ...idxCols];
  }, [data, theme]);

  const count = data?.read_portal_idx?.count;

  const effectiveCount = useMemo(() => {
    return search !== '' ? rows.length : count ?? 0;
  }, [rows.length, count, search]);

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        height: fullScreen
          ? 'calc(100dvh - 57px - 55px)'
          : 'calc(90dvh - 57px - 55px - 32px)',
        maxWidth: fullScreen ? '100vw' : '95vw',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            flexGrow: 1,
          }}
        >
          {loading
            ? `- ${t('rows')}`
            : search !== ''
            ? `${rows.length}/${count ?? '-'} ${t('rows')}`
            : `${count ?? '-'} ${t('rows')}`}
          {data?.read_portal_idx?.truncated && (
            <span> {t('truncated to the first 700 rows')}</span>
          )}
        </Typography>
        <SearchInput
          placeholder={t('Search by ref_object')}
          fullWidth={false}
          onValueChanged={val => setSearch(val)}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshCwIcon />}
          onClick={handleFetch}
          loading={loading}
          loadingPosition="start"
        >
          {t('Refresh')}
        </Button>
        {onToggleFullScreen != null && (
          <Tooltip title={fullScreen ? t('Exit fullscreen') : t('Fullscreen')}>
            <IconButton onClick={onToggleFullScreen}>
              {fullScreen ? (
                <MinimizeIcon size={20} />
              ) : (
                <MaximizeIcon size={20} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {error != null && <Alert severity="error">{error.message}</Alert>}
      <RaDataGrid
        rows={rows}
        columns={columns}
        getRowId={row => row._id}
        rowCount={effectiveCount}
        loading={loading}
        paginationMode="client"
        sortingMode="client"
        density="compact"
        disableRowSelectionOnClick
        disableColumnSelector={false}
        queryParamsScope="idx"
        autoHeight={false}
        onRowClick={() => {}}
      />
    </Stack>
  );
};
